import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaUser, FaEnvelope, FaPhone, FaLock, FaCalendarAlt, FaChevronLeft } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../baseurl";
import "./HotelFullDetails.css";

const PreCheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hotel, room, sessionId, searchDetails } = location.state || {};

  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [timer, setTimer] = useState(300); // 5 minutes
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!room || !hotel) {
      navigate("/");
      return;
    }

    const countdown = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [room, hotel, navigate]);

  const handleTimeout = async () => {
    alert("Session expired! Your room lock has been released.");
    try {
      await axios.post(`${baseurl}/releaseRoomLock`, { session_id: sessionId });
    } catch (e) {
      console.error("Release failed", e);
    }
    navigate(-1);
  };

  const handleGoBack = async () => {
    if (window.confirm("Do you want to cancel this booking and go back?")) {
      try {
        await axios.post(`${baseurl}/releaseRoomLock`, { session_id: sessionId });
      } catch (e) {}
      navigate(-1);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!customer.name.trim()) errors.name = "Full name is required";
    if (!customer.email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) {
      errors.email = "Please enter a valid email";
    }
    if (!customer.phone.trim()) errors.phone = "Phone number is required";
    else if (!/^\d{10,}$/.test(customer.phone.replace(/\D/g, ''))) {
      errors.phone = "Please enter a valid phone number";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFinalSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      console.log("Submit Booking:", { customer, hotel, room, sessionId });
      alert("Proceeding to Payment...");
    }
  };


  const handleBookingSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Navigate to the final checkout page with all accumulated data
      navigate("/checkout", {
        state: { 
          hotel, 
          room, // Using 'room' from your component's destructuring
          customer, 
          sessionId, 
          searchDetails, 
          hotel_id: hotel.id 
        }
      });
    }
  };
  

  if (!room) return null;

  const nights = Math.max(
    1, 
    Math.round(
      (new Date(searchDetails.checkOut) - new Date(searchDetails.checkIn)) / 86400000
    )
  );

  const totalPrice = (room.displayPrice * nights).toLocaleString('en-IN');
  const minutesLeft = Math.floor(timer / 60);
  const secondsLeft = String(timer % 60).padStart(2, '0');

  return (
    <div className="checkout-wrapper">
      {/* Navigation */}
      <div className="checkout-nav">
        <div className="go-container">
          <button onClick={handleGoBack} className="btn-back">
            <FaChevronLeft /> Back to Details
          </button>
        </div>
      </div>

      {/* Main Checkout Grid */}
      <div className="go-container checkout-grid">
        {/* Left: Form Section */}
        <div className="checkout-main">
          {/* Timer Banner */}
          <div className="timer-banner">
            <FaLock /> Room secured! We're holding this for {minutesLeft}:{secondsLeft}
          </div>

          {/* Checkout Form Card */}
          <section className="checkout-card">
            <h3>Enter Your Details</h3>
            <form onSubmit={handleBookingSubmit}>
              {/* Full Name Field */}
              <div className="form-group">
                <label htmlFor="fullname">
                  <FaUser /> Full Name
                </label>
                <input 
                  id="fullname"
                  type="text" 
                  required 
                  value={customer.name}
                  onChange={(e) => {
                    setCustomer({...customer, name: e.target.value});
                    setFormErrors({...formErrors, name: ""});
                  }} 
                  placeholder="John Doe"
                  aria-describedby={formErrors.name ? "name-error" : undefined}
                />
                {formErrors.name && (
                  <small id="name-error" style={{ color: '#e74c3c' }}>
                    {formErrors.name}
                  </small>
                )}
              </div>

              {/* Email Field */}
              <div className="form-group">
                <label htmlFor="email">
                  <FaEnvelope /> Email Address
                </label>
                <input 
                  id="email"
                  type="email" 
                  required 
                  value={customer.email}
                  onChange={(e) => {
                    setCustomer({...customer, email: e.target.value});
                    setFormErrors({...formErrors, email: ""});
                  }} 
                  placeholder="john@example.com"
                  aria-describedby={formErrors.email ? "email-error" : undefined}
                />
                {formErrors.email && (
                  <small id="email-error" style={{ color: '#e74c3c' }}>
                    {formErrors.email}
                  </small>
                )}
              </div>

              {/* Phone Field */}
              <div className="form-group">
                <label htmlFor="phone">
                  <FaPhone /> Phone Number
                </label>
                <input 
                  id="phone"
                  type="tel" 
                  required 
                  value={customer.phone}
                  onChange={(e) => {
                    setCustomer({...customer, phone: e.target.value});
                    setFormErrors({...formErrors, phone: ""});
                  }} 
                  placeholder="+91 00000 00000"
                  aria-describedby={formErrors.phone ? "phone-error" : undefined}
                />
                {formErrors.phone && (
                  <small id="phone-error" style={{ color: '#e74c3c' }}>
                    {formErrors.phone}
                  </small>
                )}
              </div>

              {/* Submit Button */}
              <button type="submit" className="btn-pay-now">
                Continue to Payment
              </button>
            </form>
          </section>
        </div>

        {/* Right: Summary Sidebar */}
        <aside className="checkout-sidebar">
          <div className="summary-card">
            <h3>Booking Summary</h3>

            {/* Hotel Info */}
            <div className="summary-hotel">
              <h4>{hotel.hotel_name}</h4>
              <p>{hotel.city_village}</p>
            </div>

            {/* Dates */}
            <div className="summary-dates">
              <p>
                <FaCalendarAlt /> 
                {searchDetails.checkIn} — {searchDetails.checkOut}
              </p>
              <span>{nights} Night{nights !== 1 ? 's' : ''}</span>
            </div>

            {/* Room Info */}
            <div className="summary-room">
              <strong>{room.name}</strong>
              <p>
                {searchDetails.adults} Adult{searchDetails.adults !== 1 ? 's' : ''}, 
                {' '}{searchDetails.children} Child{searchDetails.children !== 1 ? 'ren' : ''}
              </p>
            </div>

            <hr />

            {/* Price Breakdown (Optional) */}
            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-sm)',
                fontSize: '0.9rem'
              }}>
                <span>₹{room.displayPrice?.toLocaleString('en-IN')} × {nights} night{nights !== 1 ? 's' : ''}</span>
                <span>₹{totalPrice}</span>
              </div>
            </div>

            {/* Total Price */}
            <div className="summary-total">
              <span>Total Price</span>
              <span className="amount">₹{totalPrice}</span>
            </div>
            <p className="tax-hint">Inc. of all service markups</p>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default PreCheckoutPage;